/**
 * 用户管理API
 * 使用RTK Query进行用户管理相关的API调用
 */

import { apiSlice } from './apiSlice';
import type {
  User,
  UserListResponse,
  UserResponse,
  ResetPasswordResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ResetPasswordRequest,
  ResendActivationRequest,
  ResendActivationResponse,
  UpdateUserStatusRequest,
  UserQueryParams,
} from '../../types/user-management';

// 构建查询参数字符串
const buildQueryString = (params: UserQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.search) searchParams.append('search', params.search);
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

  // 处理数组参数
  if (params.role && params.role.length > 0) {
    params.role.forEach(role => searchParams.append('role', role));
  }

  if (params.status && params.status.length > 0) {
    params.status.forEach(status => searchParams.append('status', status));
  }

  // 处理布尔值参数
  if (typeof params.isBuiltIn !== 'undefined') {
    searchParams.append('isBuiltIn', params.isBuiltIn.toString());
  }

  // 处理日期范围参数
  if (params.lastLoginFrom) {
    searchParams.append('lastLoginFrom', params.lastLoginFrom);
  }
  if (params.lastLoginTo) {
    searchParams.append('lastLoginTo', params.lastLoginTo);
  }

  return searchParams.toString();
};

export const userManagementApi: any = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // 获取用户列表
    getUsers: builder.query<UserListResponse, UserQueryParams>({
      query: (params = {}) => {
        const queryString = buildQueryString(params);
        return {
          url: `/users${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'User' as const, id: 'LIST' },
        ...(result?.data?.users?.map(({ _id }) => ({
          type: 'User' as const,
          id: _id,
        })) ?? []),
      ],
      // 设置缓存时间为30秒
      keepUnusedDataFor: 30,
    }),

    // 获取单个用户详情
    getUser: builder.query<UserResponse, string>({
      query: userId => ({
        url: `/users/${userId}`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),

    // 创建用户
    createUser: builder.mutation<
      UserResponse & { generatedPassword?: string },
      CreateUserRequest
    >({
      query: userData => ({
        url: '/users',
        method: 'POST',
        body: userData,
      }),
      // Bug修复: 新增用户后同时刷新列表和统计数据
      invalidatesTags: [
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'STATISTICS' },
      ],
      // 乐观更新：立即更新缓存
      onQueryStarted: async (userData, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          // 更新用户列表缓存
          dispatch(
            userManagementApi.util.updateQueryData(
              'getUsers',
              {},
              (draft: any) => {
                if (draft.data && data.data) {
                  draft.data.users.unshift(data.data.user);
                  draft.data.total += 1;
                }
              }
            )
          );
        } catch {
          // 如果失败，不做任何操作，RTK Query会自动回滚
        }
      },
    }),

    // 更新用户信息
    updateUser: builder.mutation<UserResponse, UpdateUserRequest>({
      query: ({ id, ...userData }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: userData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
      // 乐观更新
      onQueryStarted: async (
        { id, ...userData },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          userManagementApi.util.updateQueryData(
            'getUsers',
            {},
            (draft: any) => {
              if (draft.data) {
                const user = draft.data.users.find((u: any) => u._id === id);
                if (user) {
                  Object.assign(user, userData);
                  user.updatedAt = new Date().toISOString();
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // 重置用户密码
    resetUserPassword: builder.mutation<
      ResetPasswordResponse,
      ResetPasswordRequest
    >({
      query: ({ id, ...resetData }) => ({
        url: `/users/${id}/reset-password`,
        method: 'POST',
        body: resetData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }],
    }),

    // 补发激活邮件（仅待激活用户）
    resendActivation: builder.mutation<
      ResendActivationResponse,
      ResendActivationRequest
    >({
      query: ({ id }) => ({
        url: `/users/${id}/resend-activation`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    // 更新用户状态
    updateUserStatus: builder.mutation<UserResponse, UpdateUserStatusRequest>({
      query: ({ id, ...statusData }) => ({
        url: `/users/${id}/status`,
        method: 'PUT',
        body: statusData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
      // 乐观更新
      onQueryStarted: async ({ id, status }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          userManagementApi.util.updateQueryData(
            'getUsers',
            {},
            (draft: any) => {
              if (draft.data) {
                const user = draft.data.users.find((u: any) => u._id === id);
                if (user) {
                  user.status = status;
                  user.updatedAt = new Date().toISOString();
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // 批量操作用户状态
    batchUpdateUserStatus: builder.mutation<
      { success: boolean; updated: number; failed: string[] },
      { userIds: string[]; status: string; reason?: string }
    >({
      queryFn: async ({ userIds, status, reason }, api) => {
        const results: PromiseSettledResult<any>[] = await Promise.allSettled(
          userIds.map(id =>
            api
              .dispatch(
                userManagementApi.endpoints.updateUserStatus.initiate({
                  id,
                  status: status as any,
                  reason,
                })
              )
              .unwrap()
          )
        );

        const updated = results.filter(
          (r: any) => r.status === 'fulfilled'
        ).length;
        const failed: string[] = results
          .filter((r: any) => r.status === 'rejected')
          .map((_: any, index: number) => userIds[index]);

        return {
          data: {
            success: failed.length === 0,
            updated,
            failed,
          },
        };
      },
      // Bug修复: 批量更新状态后同时刷新统计数据
      invalidatesTags: [
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'STATISTICS' },
      ],
    }),

    // 删除用户（软删除）
    deleteUser: builder.mutation<UserResponse, string>({
      query: userId => ({
        url: `/users/${userId}/status`,
        method: 'PUT',
        body: { status: 'deleted', reason: '管理员删除' },
      }),
      // Bug修复: 删除用户后同时刷新统计数据
      invalidatesTags: (result, error, userId) => [
        { type: 'User', id: userId },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'STATISTICS' },
      ],
    }),

    // 获取用户统计信息
    getUserStatistics: builder.query<
      {
        total: number;
        byRole: Record<string, number>;
        byStatus: Record<string, number>;
        activeToday: number;
        newUsersThisMonth: number;
      },
      void
    >({
      queryFn: async (arg, api) => {
        try {
          // 通过获取用户列表来计算统计信息
          const result = await api
            .dispatch(
              userManagementApi.endpoints.getUsers.initiate({ limit: 1000 })
            )
            .unwrap();

          if (!result.success || !result.data) {
            return {
              error: { status: 500, data: 'Failed to fetch statistics' },
            };
          }

          const users = result.data.users;
          const now = new Date();
          const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );
          const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

          const stats = {
            total: users.length,
            byRole: {} as Record<string, number>,
            byStatus: {} as Record<string, number>,
            activeToday: 0,
            newUsersThisMonth: 0,
          };

          users.forEach((user: User) => {
            // 统计角色分布
            stats.byRole[user.role] = (stats.byRole[user.role] || 0) + 1;

            // 统计状态分布
            stats.byStatus[user.status] =
              (stats.byStatus[user.status] || 0) + 1;

            // 统计今日活跃用户
            if (user.lastLoginAt && new Date(user.lastLoginAt) >= today) {
              stats.activeToday++;
            }

            // 统计本月新用户
            if (user.createdAt && new Date(user.createdAt) >= thisMonth) {
              stats.newUsersThisMonth++;
            }
          });

          return { data: stats };
        } catch (error) {
          return { error: { status: 500, data: error } };
        }
      },
      providesTags: [{ type: 'User', id: 'STATISTICS' }],
      keepUnusedDataFor: 60, // 缓存1分钟
    }),
  }),
});

// 导出自动生成的hooks
export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useResetUserPasswordMutation,
  useResendActivationMutation,
  useUpdateUserStatusMutation,
  useBatchUpdateUserStatusMutation,
  useDeleteUserMutation,
  useGetUserStatisticsQuery,

  // 懒加载hooks
  useLazyGetUsersQuery,
  useLazyGetUserQuery,
  useLazyGetUserStatisticsQuery,
} = userManagementApi;

// 导出API slice用于手动使用
export default userManagementApi;
